import React, {useRef} from 'react';
import PropTypes from 'prop-types';
import {Form} from 'react-final-form';
import createFocusDecorator from 'final-form-focus';
import ReCAPTCHA from 'react-google-recaptcha';
import {Box, Button, Checkbox, Grid, Link, Skeleton} from '@tenancy.nz/ui';
import CheckboxField from './CheckboxField';
import SelectionGroup from './SelectionGroup';
import Error from './Error';
import Field from './Field';
import MessageNoViewing from './MessageNoViewing';
import * as consts from '../utils/constants/form';
import {
  composeValidators,
  env,
  isEmailAddress,
  isRequired,
  isRequiredBool,
  parseToLowerCase,
  getFieldProps,
} from '../utils/helpers';

const focusOnError = createFocusDecorator();

function BookingForm({
  captcha = true,
  initialValues = undefined,
  loading = false,
  onSubmit,
  viewings = [],
}) {
  const reCaptchaRef = useRef();

  const handleFormSubmit = async (values, form, callback) => {
    if (reCaptchaRef.current.getValue()) {
      reCaptchaRef.current.reset();
    }
    const token = await reCaptchaRef.current.executeAsync();
    return onSubmit(
      {
        ...values,
        [consts.FORM_FIELD_RECAPTCHA]: token,
      },
      form,
      callback
    );
  };

  return (
    <Form
      initialValues={initialValues}
      decorators={[focusOnError]}
      onSubmit={captcha ? handleFormSubmit : onSubmit}
      render={({
        handleSubmit,
        submitting,
        submitError,
        values,
        dirtyFieldsSinceLastSubmit,
        form,
      }) => (
        <form onSubmit={handleSubmit} noValidate>
          <Error content={submitError} />
          <Grid container spacing={5}>
            {!loading && viewings.length > 0 && (
              <Grid item xs={12}>
                <Box
                  sx={{
                    opacity: values[consts.FORM_FIELD_IS_ENQUIRY] ? 0.4 : 1,
                  }}>
                  <Field
                    name={consts.FORM_FIELD_VIEWING_DAYS}
                    key={`${
                      consts.FORM_FIELD_VIEWING_DAYS
                    }_${!!values[consts.FORM_FIELD_IS_ENQUIRY]}`}
                    validate={
                      values[consts.FORM_FIELD_IS_ENQUIRY] ? null : isRequired
                    }
                    type="radio"
                    Component={SelectionGroup}
                    label="Choose a Viewing Date"
                    required={!values[consts.FORM_FIELD_IS_ENQUIRY]}
                    disabled={!!values[consts.FORM_FIELD_IS_ENQUIRY]}
                    options={viewings}
                    value={values[consts.FORM_FIELD_VIEWING_DAYS]}
                  />
                </Box>
                <Field name={consts.FORM_FIELD_IS_ENQUIRY} type="checkbox">
                  {({input, meta}) => (
                    <Checkbox
                      {...getFieldProps({input, meta})}
                      filledOnChecked
                      label="Viewing Times Not Suitable"
                      color="primary"
                      onChange={e => {
                        input.onChange(e);
                        if (e.target.checked) {
                          form.change(
                            consts.FORM_FIELD_VIEWING_DAYS,
                            undefined
                          );
                        }
                      }}
                    />
                  )}
                </Field>
              </Grid>
            )}
            {!loading && viewings.length === 0 && (
              <Grid item xs={12}>
                <MessageNoViewing />
                <Field
                  name={consts.FORM_FIELD_IS_ENQUIRY}
                  defaultValue="1"
                  value="1">
                  {({input}) => (
                    <input
                      type="hidden"
                      name={input.name}
                      value={input.value || '1'}
                    />
                  )}
                </Field>
              </Grid>
            )}
            <Grid item xs={12}>
              <Field
                name={consts.FORM_FIELD_FIRST_NAME}
                validate={isRequired}
                label="First Name"
                required
              />
            </Grid>
            <Grid item xs={12}>
              <Field
                name={consts.FORM_FIELD_LAST_NAME}
                validate={isRequired}
                label="Last Name"
                required
              />
            </Grid>
            <Grid item xs={12}>
              <Field
                name={consts.FORM_FIELD_EMAIL}
                parse={parseToLowerCase}
                validate={composeValidators(isRequired, isEmailAddress)}
                label="Email"
                type="email"
                required
              />
            </Grid>

            <Grid item xs={12}>
              <Field
                name={consts.FORM_FIELD_MOBILE}
                validate={isRequired}
                label="Mobile"
                type="tel"
                required
                InputProps={
                  values[consts.FORM_FIELD_IS_OVERSEAS_PHONE]
                    ? {}
                    : {
                        startAdornment: (
                          <span style={{marginRight: -12}}>+64</span>
                        ),
                      }
                }
                style={{marginBottom: '12px'}}
              />
              <CheckboxField
                name={consts.FORM_FIELD_IS_OVERSEAS_PHONE}
                type="checkbox"
                label="Don’t have a New Zealand number"
              />
            </Grid>
            <Grid item xs={12}>
              <Field
                name={consts.FORM_FIELD_TOTAL_ATTENDEES}
                validate={isRequired}
                label="Number of People Attending"
                required
                inputProps={{
                  inputMode: 'numeric',
                }}
                {...(dirtyFieldsSinceLastSubmit[
                  consts.FORM_FIELD_VIEWING_DAYS
                ] && {
                  errorMessage: '',
                  showError: false,
                })}
              />
            </Grid>

            {loading && (
              <Grid item xs={12}>
                <Skeleton height={30} width="55%" />
                <Skeleton height={30} width="45%" />
                <Skeleton height={30} width="55%" />
              </Grid>
            )}
            <Grid item xs={12}>
              <Field
                name={consts.FORM_FIELD_COMMENTS}
                key={`${consts.FORM_FIELD_COMMENTS}_${!!values[
                  consts.FORM_FIELD_IS_ENQUIRY
                ]}`}
                validate={
                  values[consts.FORM_FIELD_IS_ENQUIRY] ? isRequired : null
                }
                label="Comments / Questions"
                multiline
                required={!!values[consts.FORM_FIELD_IS_ENQUIRY]}
                rows={6}
              />
            </Grid>
            <Grid item xs={12}>
              <CheckboxField
                name={consts.FORM_FIELD_TERMS}
                validate={isRequiredBool}
                label={
                  <span>
                    I agree to the{' '}
                    <Link
                      href="https://tenant.co.nz/privacy-policy"
                      target="_blank"
                      underline="always"
                      rel="noopener noreferrer">
                      privacy consents
                    </Link>{' '}
                    and agree to receive text alerts relating to property
                    viewings.
                  </span>
                }
              />
            </Grid>
            {captcha && (
              <Grid item xs={12} style={{zIndex: 2}}>
                <ReCAPTCHA
                  sitekey={env('REACT_APP_GOOGLE_RECAPTCHA_SITE_KEY')}
                  ref={reCaptchaRef}
                  size="invisible"
                />{' '}
              </Grid>
            )}

            <Grid item xs={12}>
              <Button
                type="submit"
                size="large"
                color="primary"
                loading={submitting || loading}
                fullWidth>
                {values[consts.FORM_FIELD_IS_ENQUIRY] ? 'Submit' : 'Book'}
              </Button>
            </Grid>
          </Grid>
        </form>
      )}
    />
  );
}

BookingForm.propTypes = {
  /**
   * If `true`, will enable google recaptcha
   */
  captcha: PropTypes.bool,

  /**
   * Object of values which will be initial values of form fields.
   */
  initialValues: PropTypes.object,

  /**
   * If `true`, will show loading state
   */
  loading: PropTypes.bool,

  /**
   * Function to handle form submit action.
   */
  onSubmit: PropTypes.func.isRequired,

  /**
   * List of viewing days
   */
  viewings: PropTypes.arrayOf(
    PropTypes.shape({
      label: PropTypes.string.isRequired,
      value: PropTypes.string.isRequired,
    })
  ),
};

export default BookingForm;
